import { useQuery } from '@tanstack/react-query';

import { getData, getPaginated } from '@/lib/api/client-v2';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import type { ApiList } from '@/types/api/common';
import type { Contract } from '@/types/api/contract';
import { ENDPOINTS, PDF } from '@/types/api/endpoints';
import type { Quittance } from '@/types/api/payment';
import type { Receipt, ReceiptPreviewV2, ReceiptV2 } from '@/types/api/receipt';

/**
 * Quittances : impayés de l'assuré (`GET /receipts`) et quittances d'un contrat
 * (`GET /contracts/{id}/receipts`), ramenés aux noms de la v1 que lisent les écrans.
 */

/*
 * La v1 servait la 1re page de 10 impayés (`per_page` par défaut) et les écrans comptent la liste
 * reçue (pastille du menu, badge « Cotisations à régler ») : on demande la même page.
 */
const UNPAID_PARAMS = { status: 'all', page: 1, per_page: 10 } as const;

const toUnpaidReceipt = (receipt: ReceiptPreviewV2): Quittance => ({
    id: receipt.id,
    id_belair: receipt.belair_id,
    id_contrat: receipt.contract_id === null ? undefined : String(receipt.contract_id),
    numeroPolice: receipt.policy_number ?? '',
    solde: receipt.balance,
    montant: receipt.amount,
    type: receipt.type ?? '',
    dateDebut: receipt.start_date ?? undefined,
    dateFin: receipt.end_date ?? undefined,
    pay_link: receipt.pay_link,
    pay_link_reason: receipt.pay_link_reason,
    has_tableau_prelevement: receipt.has_tableau_prelevement,
});

/**
 * Quittances impayées de l'utilisateur (`GET /receipts?status=all`).
 *
 * `data` garde la forme du corps v1 (`{ data: Receipt[], meta }`), que les écrans lisent tel quel.
 * Un échec est signalé par une popin, comme auparavant.
 */
export const useReceiptsUnpaid = (enabled = true) =>
    useQuery({
        queryKey: queryKeys.receiptsUnpaid,
        queryFn: () =>
            getPaginated<ReceiptPreviewV2[]>(ENDPOINTS.receipts, { ...UNPAID_PARAMS })
                .then(({ data, meta }): ApiList<Quittance> => ({ data: data.map(toUnpaidReceipt), meta: { ...meta } }))
                .catch((error: unknown) => {
                    void notify({
                        html: '<p>Une erreur est survenue lors de la récupération des quittances impayés.</p>',
                        icon: 'error',
                        title: 'Attention !',
                        confirmButtonText: "J'ai compris",
                    });
                    throw error;
                }),
        enabled,
    });

/** Quittances d'un contrat donné parmi celles de `useReceiptsUnpaid` (ancien `getDataReceiptsContractUnpaid`). */
export const filterReceiptsOfContract = (receipts: ApiList<Receipt> | undefined, contractId: string | number | undefined) =>
    (receipts?.data ?? []).filter((receipt) => receipt.id_contrat === String(contractId));

/*
 * Ordre de la v1 (`ORDER BY b_debeffet DESC, B_PIECE DESC`) : la v2 trie par bordereau, or la
 * « dernière quittance » (1re de la liste) porte le mémo, l'attestation et le tableau de
 * prélèvement. Dates `Y-m-d H:i:s` comparables en texte ; une date absente passe en fin, comme
 * le `DESC` de Firebird.
 */
const compareV1Order = (a: ReceiptV2, b: ReceiptV2): number => {
    if (a.start_date !== b.start_date) {
        if (a.start_date === null) return 1;
        if (b.start_date === null) return -1;
        return a.start_date < b.start_date ? 1 : -1;
    }

    return Number(b.piece ?? 0) - Number(a.piece ?? 0);
};

const toContractReceipt = (receipt: ReceiptV2, contractId: string | number): Quittance => ({
    id: receipt.id,
    id_belair: receipt.belair_id,
    id_contrat: String(contractId),
    solde: receipt.balance,
    montant: receipt.amount,
    type: receipt.type ?? '',
    dateDebut: receipt.start_date ?? undefined,
    dateFin: receipt.end_date ?? undefined,
    pay_link: receipt.pay_link,
    pay_link_reason: receipt.pay_link_reason,
    has_tableau_prelevement: receipt.has_tableau_prelevement,
    // La v1 servait ces deux liens sur toute quittance ; l'attestation RC circuit dépend du
    // contrat (cf. `getRcCircuitLink`).
    memo_link: PDF.receiptMemo(contractId, receipt.belair_id),
    attestation_assurance_link: PDF.receiptAttestation(contractId, receipt.belair_id),
});

/**
 * Quittances d'un contrat (`GET /contracts/{id}/receipts`), dans l'ordre de la v1 (ancien
 * `state.receipts.data`). Relu à chaque montage ; popin à l'échec, comme auparavant.
 */
export const useContractReceipts = (contractId: string | number | null | undefined) =>
    useQuery({
        queryKey: queryKeys.contractReceipts(contractId),
        queryFn: () =>
            getData<ReceiptV2[]>(ENDPOINTS.contractReceipts(contractId!))
                .then((receipts) => [...receipts].sort(compareV1Order).map((receipt) => toContractReceipt(receipt, contractId!)))
                .catch((error: unknown) => {
                    void notify({
                        html: '<p>Une erreur est survenue lors de la récupération des quittances payées.</p>',
                        icon: 'error',
                        title: 'Attention !',
                        confirmButtonText: "J'ai compris",
                    });
                    throw error;
                }),
        enabled: contractId !== null && contractId !== undefined && contractId !== '',
    });

/** Dernière quittance (ancien sélecteur `getLastReceipt`). */
export const getLastReceipt = (receipts: Quittance[] | undefined) => receipts?.[0];

/* Types de véhicule moto ouvrant l'attestation RC circuit (`Receipt::getAttestationRcCircuitLinkAttribute`). */
const RC_CIRCUIT_MOTO_TYPES = ['2', '3', '6', '7', '8', '10', '12'];

/**
 * Attestation RC circuit d'une quittance (ancien `attestation_rc_circuit_link`) : même règle que
 * la v1 — moto verte, ou moto d'un type « piste » —, lue sur la fiche contrat.
 */
export const getRcCircuitLink = (contract: Contract | undefined, receipt: Quittance | undefined): string | null => {
    const codeProduit = contract?.vehicule?.codeProduit;
    const type = contract?.vehicule?.type;
    const eligible =
        codeProduit === '7311_PVE' ||
        (codeProduit === '7311_MOT1' && typeof type === 'string' && RC_CIRCUIT_MOTO_TYPES.includes(type));

    if (!eligible || contract?.id === undefined || receipt?.id_belair === undefined) return null;

    return PDF.receiptRcCircuit(contract.id, receipt.id_belair);
};
